import * as React from "react";
import {
    GridRowsProp,
    DataGrid,
    GridCallbackDetails,
    GridColDef,
    GridSortModel,
    GridSortModelParams,
    GridValueFormatterParams
} from "@mui/x-data-grid";

import moment from "moment";
import localisations from "domains/partitions/localisation";
import { guid, QueryPartitionsResponse, queryPartitions } from "domains/partitions/services/partition-service";
import { appInsights } from "../../../../app-insights";
import { SeverityLevel } from "@microsoft/applicationinsights-web";
import PartitionSearch from '../partition-search/PartitionSearch';

type PartitionTableProps = {
    fieldCompanyId: number;
    onRowClick?: (partitionId: guid) => void | null;
};

type PartitionSearchProps = {
    searchBy?: number;
    searchText: string;
};

function GetColumns() {
    let {
        columns: { name, reference, ownerEmail, createdDateTime },
    } = localisations.partitionTable;

    let columnDefinitions = [
        {
            field: "name",
            headerName: name.headerName,
            description: name.description,
            flex: 0.3,
        },
        {
            field: "reference",
            headerName: reference.headerName,
            description: reference.description,
            flex: 0.2,
        },
        {
            field: "ownerEmail",
            headerName: ownerEmail.headerName,
            description: ownerEmail.description,
            flex: 0.3,
        },
        {
            field: "createdDateTime",
            headerName: createdDateTime.headerName,
            description: createdDateTime.description,
            flex: 0.2,
            valueFormatter: (params: GridValueFormatterParams) =>
                moment(params.value as string).format("DD/MM/YY HH:mm"),
        },
    ] as GridColDef[];

    return columnDefinitions;
}

function PartitionTable(props: PartitionTableProps) {
    const [page, setPage] = React.useState(0);
    const [pageSize, setPageSize] = React.useState(10);
    const [sortModel, setSortModel] = React.useState<GridSortModel>([{ field: "name", sort: "asc" }]);
    const [searchDetails, setSearchDetails] = React.useState<PartitionSearchProps>({ searchBy: 1, searchText: "" });

    const [rows, setRows] = React.useState<GridRowsProp>([]);
    const [rowCount, setRowCount] = React.useState<number | null>(0);
    const [loading, setLoading] = React.useState<boolean>(false);

    const handlePageChange = (pageNumber: number, _: GridCallbackDetails) => {
        if (page !== pageNumber) {
            setPage(pageNumber);
        }
    };

    const handlePageSizeChange = (pageSizeNumber: number, _: GridCallbackDetails) => {
        if (pageSize !== pageSizeNumber) {
            setPage(0);
            setPageSize(pageSizeNumber);            
        }
    };

    const handleSortModelChange = (model: GridSortModel, _: GridCallbackDetails) => {
        if (sortModel !== model) {
            setPage(0);
            setSortModel(model);
        }
    };
    
    React.useEffect(() => {
        let active = true;

        (async () => {
            setLoading(true);
            try {
                appInsights.trackTrace({ message: "Querying Partition Api", severityLevel: SeverityLevel.Information });
    
                let queryResponse: QueryPartitionsResponse;

                let name = searchDetails.searchBy === 1 ? searchDetails.searchText : null;
                let reference = searchDetails.searchBy === 2 ? searchDetails.searchText : null;

                queryResponse = await queryPartitions({
                    fieldCompanyId: props.fieldCompanyId,
                    pageToken: (page + 1).toString(),
                    itemsPerPage: pageSize,
                    name,
                    reference,
                    sortOrder: sortModel.map((s) => `${s.field} ${s.sort}`).join(","),
                });
                
                // Active flag here presumably prevents async code from executing if the page is disposed.
                if (!active) {
                    return;
                }
    
                setRowCount(queryResponse.totalCount);
                setRows(queryResponse.items);
                setLoading(false);
                
            } catch (ex) {
                alert(ex);
            }
        })();
        
        // Set Active flag false if this is diposed. This is relevant if an Async call is in progress whilst page is disposed.
        return () => {
            active = false;
        }

    }, [page, pageSize, sortModel, props.fieldCompanyId, searchDetails]);

    return (
        <div className="partition-table" style={{ height: 800, width: "100%" }}>
            <PartitionSearch handleSearch={(searchBy, searchText) => {
                setPage(0);
                setSearchDetails({ searchBy, searchText });
            } } />
            
            <DataGrid
                rows={rows}
                columns={GetColumns()}
                page={page}
                pageSize={pageSize}
                rowsPerPageOptions={[5, 10, 20]}
                loading={loading}
                onRowClick={(row) => {
                    if (props.onRowClick) {
                        props.onRowClick(row.id.toString());
                    }
                }}                
                rowCount={rowCount == null ? undefined : rowCount}
                paginationMode="server"
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                sortingMode="server"
                sortModel={sortModel}
                onSortModelChange={handleSortModelChange}
                disableColumnFilter
            />
        </div>
    );
}

export default PartitionTable;
